import { useCallback, useEffect, useState } from 'react';

export default function useTwoPageScale(
	containerRef,
	firstPageSizeRef,
	gap = 20,
	isSinglePage = false,
	pdfDocument = null,
	preferWidthFit = false
) {
	const [baseScale, setBaseScale] = useState(0.5);

	const computeBaseScale = useCallback(() => {
		if (!containerRef.current || !firstPageSizeRef.current) return;
		const rect = containerRef.current.getBoundingClientRect();
		const containerW = rect.width;
		const containerH = rect.height;
		const naturalW = firstPageSizeRef.current.width;
		const naturalH = firstPageSizeRef.current.height;

		// Don't calculate if container doesn't have valid dimensions yet
		if (containerW <= 0 || containerH <= 0) return;

		// Calculate scale based on width
		const targetWPerPage = isSinglePage ? Math.max(1, containerW - gap * 2) : Math.max(1, (containerW - gap) / 2);
		const scaleByWidth = targetWPerPage / naturalW;

		if (preferWidthFit) {
			setBaseScale(scaleByWidth);
			return;
		}

		// Calculate scale based on height (with some padding)
		const targetH = Math.max(1, containerH - gap * 2);
		const scaleByHeight = targetH / naturalH;

		// Use the smaller scale to ensure it fits both width and height
		const finalScale = Math.min(scaleByWidth, scaleByHeight);

		setBaseScale(finalScale);
	}, [containerRef, firstPageSizeRef, gap, isSinglePage, preferWidthFit]);

	useEffect(() => {
		if (!containerRef.current) return;
		const ro = new ResizeObserver(computeBaseScale);
		ro.observe(containerRef.current);
		return () => ro.disconnect();
	}, [computeBaseScale, containerRef]);

	// Compute base scale when PDF document becomes available
	useEffect(() => {
		if (pdfDocument && firstPageSizeRef.current && containerRef.current) {
			computeBaseScale();
		}
	}, [pdfDocument, computeBaseScale]);

	return { baseScale, computeBaseScale };
}

